import { cn } from '@exam-room/utils';
import { useEffect, useState, useRef, type ReactElement } from 'react';

import type { SnackbarMessage, SnackbarVariant } from '../../providers/snackbar/types';
import { CheckCircleRoundedIcon, InfoRoundedIcon } from '../icons';

interface SnackbarItemProps {
  snackbar: SnackbarMessage;
  onClose: (id: string) => void;
}

const variantStyles: Record<
  SnackbarVariant,
  {
    bg: string;
    border: string;
    text: string;
    iconColor: string;
    icon: ReactElement;
    progressBar: string;
  }
> = {
  success: {
    bg: 'bg-success-100',
    border: 'border-success-500',
    text: 'text-success-900',
    iconColor: 'text-success-500',
    icon: <CheckCircleRoundedIcon />,
    progressBar: 'bg-success-500',
  },
  error: {
    bg: 'bg-error-100',
    border: 'border-danger-500',
    text: 'text-danger-900',
    iconColor: 'text-danger-500',
    icon: <InfoRoundedIcon />,
    progressBar: 'bg-danger-500',
  },
  warning: {
    bg: 'bg-warning-100',
    border: 'border-warning-500',
    text: 'text-warning-900',
    iconColor: 'text-warning-500',
    icon: <InfoRoundedIcon />,
    progressBar: 'bg-warning-500',
  },
  info: {
    bg: 'bg-info-100',
    border: 'border-info-700',
    text: 'text-info-900',
    iconColor: 'text-info-500',
    icon: <InfoRoundedIcon />,
    progressBar: 'bg-info-700',
  },
  default: {
    bg: 'bg-grey-100',
    border: 'border-grey-500',
    text: 'text-grey-900',
    iconColor: 'text-grey-500',
    icon: <InfoRoundedIcon />,
    progressBar: 'bg-grey-500',
  },
};

function LoadingSpinner() {
  return (
    <div className='h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent' />
  );
}

export function SnackbarItem({ snackbar, onClose }: SnackbarItemProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const animationFrameRef = useRef<number>();

  const variant = snackbar.variant || 'default';
  const styles = variantStyles[variant];

  useEffect(() => {
    // Trigger enter animation
    requestAnimationFrame(() => {
      setIsVisible(true);
    });

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose(snackbar.id);
    }, 300);
  };

  useEffect(() => {
    if (snackbar.autoHideDuration && !snackbar.pending) {
      timeoutRef.current = setTimeout(() => {
        handleClose();
      }, snackbar.autoHideDuration);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [snackbar.autoHideDuration, snackbar.pending, handleClose]);

  return (
    <div
      className={cn(
        'mb-2 transform transition-all duration-300 ease-out',
        isVisible && !isExiting ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0',
      )}
    >
      <div
        className={cn(
          'pointer-events-auto relative max-w-[500px] min-w-[300px] overflow-hidden rounded-xl border-[1px] shadow-lg',
          styles.bg,
          styles.border,
          styles.text,
        )}
      >
        <div className='flex items-center gap-2 p-4'>
          <div className='flex-shrink-0'>
            {snackbar.pending ? (
              <LoadingSpinner />
            ) : (
              <div
                className={cn(
                  'flex h-5 w-5 items-center justify-center rounded-full',
                  styles.iconColor,
                )}
              >
                {styles.icon}
              </div>
            )}
          </div>

          {/* Message */}
          <div className='flex-1'>
            <p className='font-semibold'>{snackbar.message}</p>
            {snackbar.action ? <div className='mt-2'>{snackbar.action}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
}
